import { Component, computed, OnInit, signal } from '@angular/core';
import { ErrorParser } from '@app/classes';
import { Platform } from '@app/classes/platform.class';
import { FeatureFlag } from '@app/enums';
import { AuthorizationDocument } from '@app/models/payroll/self-onboarding-document.model';
import { CompanyMenuService } from '@app/modules/company/services';
import { BaseForm } from '@app/modules/forms/base.form';
import { SelfOnboardingDocumentsService } from '@app/modules/payroll/services';
import {
    AuthorizationDocumentType,
    PAYROLL_AUTHORIZATION_DOCUMENTS,
} from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { PayrollService } from '@app/services';
import { AbilityService } from '@app/services/ability.service';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { ShowView } from '@common/views';
import { BankAccount } from '@models/company/bank-account.model';
import { Company } from '@models/company/company.model';
import { isEmpty, noop } from 'lodash-es';

type CompanyShowDialogType = 'COMPANY_INFO' | 'PAYROLL_INFO' | 'PAYROLL_RQ_INFO' | 'BANK_INFO';

@Component({
    templateUrl: './overview-show.template.html',
    styleUrls: ['./overview-show.style.scss'],
})
export class OverviewShowView extends ShowView implements OnInit {
    isLoading = true;
    editPermission: string[] = ['update.companyDetail'];
    payrollPermission: string[] = Platform.payroll.permission.view;
    bankAccount!: BankAccount;
    company!: Company;
    companyInitial = '';
    authorizationsDocumentsHaveBeenSubmitted = false;
    payrollEnabled = false;
    hasQcPayrollFeature = false;
    hasKYCKYBFeatureFlag = signal(false);
    hasFlinksConnect = false;
    lastPaidRecurringPayrollEndDate?: Date | null;

    currentlyOpenDialog?: CompanyShowDialogType = undefined;

    isKYBVisible = computed(() => {
        if (!this.hasKYCKYBFeatureFlag()) {
            return false;
        }
        if (!this.auth.hasPayrollAccess) {
            return false;
        }
        return this.auth.isAdmin();
    });

    constructor(
        private abilities: AbilityService,
        private auth: AuthService,
        private features: FeatureService,
        private notify: NotifyService,
        private payrollService: PayrollService,
        private selfOnboardingDocumentsService: SelfOnboardingDocumentsService,
        public companyMenuService: CompanyMenuService
    ) {
        super();
        this.payrollEnabled = this.abilities.payroll();
    }

    get shouldShowRqPayrollBanner(): boolean {
        return (
            isEmpty(this.company.quebecEnterpriseNumber) ||
            isEmpty(this.company.rqRegistration) ||
            isEmpty(this.company.rqProgramCode) ||
            isEmpty(this.company.rqReferenceNumber) ||
            isEmpty(this.company.rqRemittanceFrequencyDisplay)
        );
    }

    onSave(form?: BaseForm): void {
        const toSave: Promise<Company | BankAccount>[] = [];

        // Company
        if (this.company.isDirty()) {
            toSave.push(this.company.save());
        }

        // Bank Account
        if (this.bankAccount && this.bankAccount.isDirty()) {
            toSave.push(this.bankAccount.param('company', this.company.id).save());
        }

        this.isLoading = true;
        Promise.all(toSave)
            .then(() => {
                this.notify.success('company.overview.companySaveSuccessMessage');
                this.isLoading = false;
                this.closeDialog();
            })
            .catch((err) => {
                this.notify.error(err.message || 'company.overview.companySaveErrorMessage');
                this.isLoading = false;
                form?.addExternalError();
            });
    }

    onCancel(): void {
        if (this.company.isDirty()) {
            this.company.revert();
        }
        if (this.bankAccount.isDirty()) {
            this.bankAccount.revert();
        }
        this.closeDialog();
    }

    async ngOnInit(): Promise<void> {
        if (!this.auth.company?.id) {
            return;
        }
        if (this.payrollEnabled && this.auth.role?.name === 'Admin') {
            this.lastPaidRecurringPayrollEndDate = await this.payrollService.getLastRecurringPayrollEndDate(
                this.auth.company.id
            );

            await this.getSelfOnboardingDocuments();
        }

        const [hasQcPayrollFeature, hasKYCKYBFeatureFlag, hasFlinksConnect] = await this.features.hasMany(
            FeatureFlag.qcPayroll,
            FeatureFlag.KYC_KYB,
            FeatureFlag.flinksConnect
        );
        this.hasQcPayrollFeature = hasQcPayrollFeature;
        this.hasKYCKYBFeatureFlag.set(hasKYCKYBFeatureFlag);
        this.hasFlinksConnect = hasFlinksConnect;

        Company.with('logo')
            .find(this.auth.company.id)
            .then((company) => {
                this.company = company;
                this.setBankAccount();
                this.companyInitial = company.name.charAt(0);
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    async getSelfOnboardingDocuments(): Promise<void> {
        try {
            const authorizationDocuments: Record<AuthorizationDocumentType, AuthorizationDocument> =
                await this.selfOnboardingDocumentsService.getDocuments(PAYROLL_AUTHORIZATION_DOCUMENTS);
            this.authorizationsDocumentsHaveBeenSubmitted = Object.values(authorizationDocuments).some(
                (document) => document.state !== 'new'
            );
        } catch (error) {
            this.notify.error(ErrorParser.parse(error as Record<string, unknown>));
        }
    }

    private setBankAccount(): void {
        this.bankAccount = this.company.bankAccount || new BankAccount();
        if (this.company.bankAccountId && this.auth.isAdmin()) {
            BankAccount.param('company', this.company.id)
                .orderBy('effective_at', 'DESC')
                .find(this.company.bankAccountId)
                .then((bankAccount) => {
                    this.company.bankAccounts = [bankAccount];
                    this.bankAccount = this.company.bankAccount;
                })
                .catch(noop); // Fail silently
        }
    }
}
